import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { showToast } from '../../utils/errorHandler';

interface AtsReadabilityCardProps {
  cvId: string;
}

/**
 * ATS readability score for the Passport CV page.
 *
 * Owner-only: this is rendered exclusively in "Edit" view in MyPassport, and we
 * still surface a clear note that the score & feedback are private (never shown
 * to recruiters or explorers). Reuses the existing readability endpoint and the
 * cached score persisted on the profile.
 */
const AtsReadabilityCard: React.FC<AtsReadabilityCardProps> = ({ cvId }) => {
  const { t } = useTranslation('passport');
  const { user, apiCall } = useAuth();

  const [score, setScore] = useState<{ score: number; summary: string } | null>(null);
  const [loadingCached, setLoadingCached] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Load the cached score from the profile (only if it belongs to the current CV).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.userId) return;
      try {
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.userId}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        if (
          data.ats_readability_score !== null &&
          data.ats_readability_score !== undefined &&
          data.ats_readability_cv_id === cvId
        ) {
          setScore({ score: data.ats_readability_score, summary: data.ats_readability_summary || '' });
        } else {
          setScore(null);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoadingCached(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, cvId]);

  const analyze = async () => {
    try {
      setAnalyzing(true);
      const res = await apiCall(`${apiBase}/api/v1/readability/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_id: cvId }),
      });
      if (res.ok) {
        const data = await res.json();
        setScore({ score: data.score, summary: data.summary });
      } else {
        showToast(t('ats.analyzeError'), 'error');
      }
    } catch {
      showToast(t('ats.analyzeError'), 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loadingCached) return null;

  return (
    <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
      <div className="mb-1.5">
        <span className="text-xs sm:text-sm font-semibold text-slate-700">{t('ats.title')}</span>
      </div>
      <div className="flex items-start gap-1.5 mb-3">
        <Lock className="w-3 h-3 text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] sm:text-[11px] text-slate-500 leading-relaxed">{t('ats.privateNote')}</p>
      </div>

      {!score ? (
        <button
          onClick={analyze}
          disabled={analyzing}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-sm disabled:opacity-70 disabled:cursor-wait"
        >
          {analyzing ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{t('ats.analyzing')}</span></>
          ) : (
            <span>{t('ats.analyze')}</span>
          )}
        </button>
      ) : (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}>
          {/* Score bar */}
          <div className="relative h-4 bg-slate-200 rounded-full overflow-hidden mb-1.5">
            <div className="absolute inset-0 flex">
              <div className="w-[60%] bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400" />
              <div className="w-[40%] bg-gradient-to-r from-yellow-400 via-lime-400 to-green-400" />
            </div>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${score.score}%` }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="absolute inset-y-0 left-0 bg-black/20"
            />
            <motion.div
              initial={{ left: '0%' }}
              animate={{ left: `${score.score}%` }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg -translate-x-1/2"
            />
          </div>
          <div className="flex justify-center mb-2">
            <span
              className={`text-lg sm:text-xl font-semibold ${
                score.score >= 80 ? 'text-green-600' :
                score.score >= 60 ? 'text-yellow-600' :
                score.score >= 40 ? 'text-orange-500' :
                'text-red-600'
              }`}
            >
              {score.score}
            </span>
          </div>
          {score.summary && (
            <div className="bg-white rounded-lg p-2.5 border border-slate-200">
              <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed">💡 {score.summary}</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AtsReadabilityCard;
